import { ChangeEventTargetType, ChangeEventType, PlexSummaryChangeEventFragment } from '../../../../graphql/gen/types';
import { plainTextPreview } from '../../common/plainTextPreview';
import { useSelectedPlexPost } from '../../details/post/useSelectedPlexPost';
import { useSelectedPlexQuestion } from '../../details/question/useSelectedQuestion';

export const getActionText = (event: PlexSummaryChangeEventFragment, currentUserEmail: string | undefined): string => {
  switch (event.eventType) {
    case ChangeEventType.Create: {
      switch (event.eventTarget.__typename) {
        case 'ChangeEventTargetPost':
          if (currentUserEmail && event.updateDeltaAfter?.v1?.post?.mentions?.includes(currentUserEmail)) {
            return 'mentioned you in a new post';
          }
          return 'created a new post';
        case 'ChangeEventTargetEvent':
          return 'created a new event';
        case 'ChangeEventTargetQuestion':
          return 'created a new FAQ';
        case 'ChangeEventTargetAnswer': {
          if (currentUserEmail && event.updateDeltaAfter?.v1?.answer?.mentions?.includes(currentUserEmail)) {
            return 'mentioned you in a new answer';
          }
          return 'added a new answer';
        }
        case 'ChangeEventTargetComment': {
          const userIsMentioned =
            currentUserEmail && event.eventTarget.comment?.mentions?.find((m) => m.email === currentUserEmail);
          const targetTitle = !!event.eventTarget.comment.question
            ? event.eventTarget.comment.question?.text
            : event.eventTarget.comment.post?.title;
          const eventText = userIsMentioned ? 'mentioned you in a comment on' : 'commented on';
          return `${eventText} ${targetTitle}`;
        }
        case 'ChangeEventTargetBoard': {
          const faqCount = event.eventTarget.board.faqCount;
          return `created a new board${faqCount > 0 ? ` with ${faqCount} FAQ(s)` : ''}`;
        }
      }
      return '';
    }
    case ChangeEventType.Update: {
      switch (event.eventTarget.__typename) {
        case 'ChangeEventTargetPost':
          if (
            currentUserEmail &&
            event.updateDeltaAfter?.v1?.post?.mentions?.includes(currentUserEmail) &&
            !event.updateDeltaBefore?.v1?.post?.mentions?.includes(currentUserEmail)
          ) {
            return 'mentioned you in a post';
          }
          return 'updated post';
        case 'ChangeEventTargetEvent':
          return 'updated event';
        case 'ChangeEventTargetQuestion':
          const oldAnswerId = event.updateDeltaBefore?.v1?.question?.acceptedAnswerId;
          const newAnswerId = event.updateDeltaAfter?.v1?.question?.acceptedAnswerId;
          if (!oldAnswerId && !!newAnswerId) {
            return 'approved answer';
          }
          if (!!oldAnswerId && !newAnswerId) {
            return 'removed the approved answer';
          }
          return 'updated FAQ';
        case 'ChangeEventTargetAnswer': {
          if (
            currentUserEmail &&
            event.updateDeltaAfter?.v1?.answer?.mentions?.includes(currentUserEmail) &&
            !event.updateDeltaBefore?.v1?.answer?.mentions?.includes(currentUserEmail)
          ) {
            return 'mentioned you in an answer';
          }
          return 'updated an answer';
        }
        case 'ChangeEventTargetComment':
          if (!!event.eventTarget.comment.question) {
            return `updated comment on ${event.eventTarget.comment.question?.text}`;
          } else {
            return `updated comment on ${event.eventTarget.comment.post?.title}`;
          }
        case 'ChangeEventTargetBoard': {
          return 'updated a board';
        }
      }
      return '';
    }
    case ChangeEventType.Delete: {
      switch (event.targetType) {
        case ChangeEventTargetType.Post:
          return 'deleted post';
        case ChangeEventTargetType.Event:
          return 'deleted event';
        case ChangeEventTargetType.Question:
          return 'deleted an FAQ';
        case ChangeEventTargetType.Answer:
          return 'deleted an answer';
        case ChangeEventTargetType.Board: {
          return 'deleted a board';
        }
        case ChangeEventTargetType.Comment:
          return 'deleted a comment';
      }
      return '';
    }
  }
  return '';
};

export const getTargetName = (event: PlexSummaryChangeEventFragment): string => {
  switch (event.eventTarget.__typename) {
    case 'ChangeEventTargetPost': {
      return event.eventTarget?.post.title;
    }
    case 'ChangeEventTargetEvent': {
      return event.eventTarget?.event.title;
    }
    case 'ChangeEventTargetQuestion': {
      return event.eventTarget?.question.text;
    }
    case 'ChangeEventTargetAnswer': {
      return event.eventTarget?.answer.question.text;
    }
    case 'ChangeEventTargetComment': {
      return event.eventTarget?.comment.body
        ? plainTextPreview(
            event.eventTarget.comment.body,
            event.eventTarget.comment.mentions,
            event.eventTarget.comment.links
          )
        : '';
    }
    case 'ChangeEventTargetBoard': {
      return event.eventTarget?.board.name;
    }
  }
  return '';
};

export const useGetOnClickChangeEvent = (event: PlexSummaryChangeEventFragment): (() => void) | null => {
  const [, setSelectedPlexPost] = useSelectedPlexPost();
  const [, setSelectedPlexQuestion] = useSelectedPlexQuestion();

  switch (event.eventTarget.__typename) {
    case 'ChangeEventTargetPost': {
      const { permalink } = event.eventTarget.post;
      return (): void => {
        setSelectedPlexPost({ permalink, commentsVisible: false });
      };
    }
    case 'ChangeEventTargetQuestion': {
      const { permalink } = event.eventTarget.question;
      return (): void => {
        setSelectedPlexQuestion({ permalink, commentsVisible: false });
      };
    }
    case 'ChangeEventTargetAnswer': {
      const { permalink } = event.eventTarget.answer.question;
      return (): void => {
        setSelectedPlexQuestion({ permalink, commentsVisible: false });
      };
    }
    case 'ChangeEventTargetComment': {
      if (!!event.eventTarget?.comment) {
        if (event.eventTarget?.comment.post) {
          const { permalink } = event.eventTarget.comment.post;
          return (): void => {
            setSelectedPlexPost({ permalink, commentsVisible: true });
          };
        } else if (event.eventTarget?.comment.question) {
          const { permalink } = event.eventTarget.comment.question;
          return (): void => {
            setSelectedPlexQuestion({ permalink, commentsVisible: true });
          };
        }
      }
      break;
    }
  }
  return null;
};
